import React from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Check, ChevronDown, Loader2 } from "lucide-react";
import { cn } from "../../../../../utils/cn";
import { Switch } from "../../../../../components/ui/Switch";
import { CUSTOM_PROVIDER_ID, recommendedModelId } from "../aiModelCatalog";
import { AGENT_DEFAULT_KIND } from "../agentPayload";
import { CONTROL_CLS, MetaBadge, SECONDARY_BTN_CLS, SectionTitle } from "./primitives";

// Proveedor y modelo del agente.
//
// Dos caminos: heredar el proveedor del courier (una sola credencial para
// todos) o traer el propio — incluido un endpoint OpenAI-compatible ("custom",
// que exige base_url https). El token JAMÁS se renderiza: del backend solo
// llegan has_token/token_last4 y el input es type=password.
//
// `readOnly` (el rol no administra el proveedor, o el agente está fuera de su
// alcance) deja la sección entera sin controles operables y dice por qué: la
// credencial de la IA no es algo que un rol sin permiso deba poder mirar ni
// reescribir.

// Centinela del select "Modelo personalizado…": NUNCA viaja al backend (el
// payload siempre lleva model). El backend acepta model como string libre.
const CUSTOM_MODEL = "__custom__";

export default function ProviderSection({
  value,
  catalog,
  isNew,
  typedToken,
  onChange,
  onTokenChange,
  onTest,
  testState,
  testError,
  errors = {},
  // El editor borró el token tipeado porque cambió el proveedor: sin el aviso,
  // el campo se vacía solo y parece que se perdió lo escrito.
  tokenCleared = false,
  readOnly = false,
  baseUrlRef,
  modelRef,
  tokenRef,
}) {
  const { t } = useTranslation();
  const providers = Array.isArray(catalog?.providers) ? catalog.providers : [];
  const inherit = value.inherit_tenant === true;
  const currentProvider =
    providers.find((p) => p.id === value.kind) || providers.find((p) => p.id === AGENT_DEFAULT_KIND) || null;
  const models = currentProvider?.models || [];
  const selectedModel = models.find((m) => m.id === value.model) || null;
  const customSelected = !selectedModel;
  const isCustomEndpoint = value.kind === CUSTOM_PROVIDER_ID;
  const typed = (typedToken || "").trim();

  const canTest = !isNew && (value.has_token || !!typed) && testState !== "testing";

  // Al cambiar de proveedor se precarga su modelo recomendado, salvo que el
  // admin haya tipeado uno propio (ese se respeta).
  const handleProviderChange = (e) => {
    const nextId = e.target.value;
    const next = providers.find((p) => p.id === nextId);
    const keepTypedModel = value.model && !models.some((m) => m.id === value.model);
    onChange({
      kind: nextId,
      model: keepTypedModel ? value.model : recommendedModelId(next),
    });
  };

  // Apagar la herencia deja al agente con proveedor propio: si el modelo está
  // vacío, se precarga el recomendado del proveedor ya elegido para que el
  // select no quede en "Modelo personalizado…" con el campo en blanco (el
  // usuario tendría que adivinar qué escribir).
  const handleInheritToggle = () => {
    const nextInherit = !inherit;
    const patch = { inherit_tenant: nextInherit };
    if (!nextInherit && !value.model) patch.model = recommendedModelId(currentProvider);
    onChange(patch);
  };

  return (
    <section className="space-y-4">
      <SectionTitle>{t("Proveedor y modelo")}</SectionTitle>

      {readOnly && (
        <p id="agent-provider-readonly" className="text-[12px] md:text-[11px] text-fg-muted">
          {t("Solo un administrador puede cambiar el proveedor y la credencial.")}
        </p>
      )}

      {/* Alta hecha por un rol sin permiso sobre el proveedor: el backend crea
          el agente heredando el del courier, y se dice antes de guardar. */}
      {isNew && readOnly && inherit && (
        <p className="text-[12px] md:text-[11px] text-fg-muted">
          {t("Este agente usará el proveedor del courier.")}
        </p>
      )}

      <div className="flex items-start justify-between gap-4">
        <span className="min-w-0">
          <span className="block text-xs font-medium text-fg">
            {t("Usar el proveedor del courier")}
          </span>
          <span id="agent-inherit-help" className="block text-[12px] md:text-[11px] text-fg-muted">
            {t("El agente usa la cuenta de IA que ya configuraste en el Asistente IA. Si la cambias, este agente la sigue.")}
          </span>
        </span>
        <Switch
          size="sm"
          checked={inherit}
          onChange={handleInheritToggle}
          disabled={readOnly}
          ariaLabel={t("Usar el proveedor del courier")}
          aria-describedby={
            readOnly ? "agent-inherit-help agent-provider-readonly" : "agent-inherit-help"
          }
        />
      </div>

      {inherit ? (
        <p className="text-[12px] md:text-[11px] text-fg-muted">
          {t("Sin credencial propia: la conexión se prueba en el Asistente IA.")}
        </p>
      ) : (
        <>
          {/* Proveedor */}
          <div className="space-y-1 sm:max-w-xs">
            <label
              htmlFor="agent-provider"
              className="hd-field-label block text-xs font-medium text-fg"
            >
              {t("Proveedor")}
            </label>
            <div className="relative">
              <select
                id="agent-provider"
                value={currentProvider?.id || ""}
                onChange={handleProviderChange}
                disabled={readOnly}
                aria-describedby={readOnly ? "agent-provider-readonly" : undefined}
                className={cn(CONTROL_CLS, "appearance-none !pr-8")}
              >
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none"
                aria-hidden="true"
              />
            </div>
            {/* keyUrl del catálogo: decir dónde se crea el token ahorra el
                ticket de soporte más frecuente. */}
            {currentProvider?.keyUrl && (
              <a
                href={currentProvider.keyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block text-[12px] md:text-[11px] font-medium text-brand-600 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded"
              >
                {t("¿Dónde consigo mi API key?")}
              </a>
            )}
          </div>

          {/* Endpoint propio: URL base https, sin credenciales embebidas. */}
          {isCustomEndpoint && (
            <div className="space-y-1">
              <label
                htmlFor="agent-base-url"
                className="hd-field-label block text-xs font-medium text-fg"
              >
                {t("URL base (OpenAI-compatible)")}
              </label>
              <input
                id="agent-base-url"
                ref={baseUrlRef}
                type="url"
                inputMode="url"
                value={value.base_url}
                onChange={(e) => onChange({ base_url: e.target.value })}
                disabled={readOnly}
                placeholder="https://api.tu-proveedor.com/v1"
                aria-invalid={!!errors.base_url}
                aria-describedby={
                  errors.base_url
                    ? "agent-base-url-error"
                    : readOnly
                      ? "agent-provider-readonly"
                      : "agent-base-url-help"
                }
                className={CONTROL_CLS}
              />
              {errors.base_url ? (
                <p
                  id="agent-base-url-error"
                  className="text-[12px] md:text-[11px] text-status-error"
                  role="alert"
                >
                  {t(errors.base_url)}
                </p>
              ) : (
                <p id="agent-base-url-help" className="text-[12px] md:text-[11px] text-fg-muted">
                  {t(
                    "Debe empezar con https:// y no llevar usuario ni contraseña: el token va aparte. Si no termina en /chat/completions, se le agrega.",
                  )}
                </p>
              )}
            </div>
          )}

          {/* Modelo */}
          <div className="space-y-1 sm:max-w-xs">
            <label
              htmlFor="agent-model"
              className="hd-field-label block text-xs font-medium text-fg"
            >
              {t("Modelo")}
            </label>
            {models.length > 0 && (
              <div className="relative">
                <select
                  id="agent-model"
                  value={customSelected ? CUSTOM_MODEL : value.model}
                  onChange={(e) =>
                    onChange({ model: e.target.value === CUSTOM_MODEL ? "" : e.target.value })
                  }
                  disabled={readOnly}
                  aria-invalid={!!errors.model}
                  aria-describedby={
                    errors.model
                      ? "agent-model-error"
                      : readOnly
                        ? "agent-provider-readonly"
                        : undefined
                  }
                  className={cn(CONTROL_CLS, "appearance-none !pr-8")}
                >
                  {models.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.label}
                    </option>
                  ))}
                  <option value={CUSTOM_MODEL}>{t("Modelo personalizado…")}</option>
                </select>
                <ChevronDown
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none"
                  aria-hidden="true"
                />
              </div>
            )}

            {customSelected && (
              <input
                id="agent-model-custom"
                ref={modelRef}
                type="text"
                value={value.model}
                onChange={(e) => onChange({ model: e.target.value })}
                disabled={readOnly}
                placeholder={recommendedModelId(currentProvider) || t("Ej.: mi-modelo-1")}
                aria-label={t("Identificador del modelo")}
                aria-invalid={!!errors.model}
                aria-describedby={
                  errors.model
                    ? "agent-model-error"
                    : readOnly
                      ? "agent-provider-readonly"
                      : undefined
                }
                className={cn(CONTROL_CLS, "mt-1.5")}
              />
            )}

            {selectedModel && (selectedModel.pros || selectedModel.cons || selectedModel.badge) && (
              <p className="flex flex-wrap items-center gap-1.5 text-[12px] md:text-[11px] text-fg-muted">
                {selectedModel.badge === "recomendado" && (
                  <MetaBadge tone="brand">{t("Recomendado")}</MetaBadge>
                )}
                {selectedModel.pros && <span>{selectedModel.pros}</span>}
                {selectedModel.cons && (
                  <span className="text-fg-subtle">· {selectedModel.cons}</span>
                )}
              </p>
            )}

            {errors.model && (
              <p
                id="agent-model-error"
                className="text-[12px] md:text-[11px] text-status-error"
                role="alert"
              >
                {t(errors.model)}
              </p>
            )}
          </div>

          {/* Credencial */}
          <div className="space-y-1">
            <label
              htmlFor="agent-token"
              className="hd-field-label block text-xs font-medium text-fg"
            >
              {t("Token de API")}
            </label>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <input
                  id="agent-token"
                  ref={tokenRef}
                  type="password"
                  autoComplete="new-password"
                  value={typedToken}
                  onChange={(e) => onTokenChange(e.target.value)}
                  disabled={readOnly}
                  placeholder={
                    value.has_token
                      ? `••••••••${value.token_last4}`
                      : t("Pega aquí tu token")
                  }
                  aria-invalid={!!errors.token}
                  aria-describedby={
                    errors.token
                      ? "agent-token-error"
                      : readOnly
                        ? "agent-provider-readonly"
                        : "agent-token-help"
                  }
                  className={CONTROL_CLS}
                />
              </div>
              <button
                type="button"
                onClick={onTest}
                disabled={!canTest || readOnly}
                title={isNew ? t("Guarda el agente para probar la conexión.") : undefined}
                className={cn(SECONDARY_BTN_CLS, "shrink-0 whitespace-nowrap")}
              >
                {testState === "testing" && (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                )}
                {testState === "testing" ? t("Probando…") : t("Probar conexión")}
              </button>
            </div>

            {errors.token && (
              <p
                id="agent-token-error"
                className="text-[12px] md:text-[11px] text-status-error"
                role="alert"
              >
                {t(errors.token)}
              </p>
            )}

            {/* Resultado de la prueba: inline, nunca toast. */}
            {testState === "ok" && (
              <p
                className="inline-flex items-center gap-1 rounded bg-status-success-soft text-status-success px-1.5 py-0.5 text-xs"
                role="status"
              >
                <Check className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                {t("Conexión OK")}
              </p>
            )}
            {testState === "error" && (
              <p
                className="inline-flex items-center gap-1 rounded bg-status-error-soft text-status-error px-1.5 py-0.5 text-xs"
                role="alert"
              >
                <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                {testError}
              </p>
            )}

            {tokenCleared ? (
              <p
                id="agent-token-help"
                className="text-[12px] md:text-[11px] text-status-warning"
                role="status"
              >
                {t("Al cambiar de proveedor se borró el token que escribiste: ingrésalo de nuevo.")}
              </p>
            ) : value.has_token ? (
              <p id="agent-token-help" className="text-xs text-fg-muted">
                {t("Token guardado (termina en {{last4}}). Escribe uno nuevo para reemplazarlo.", {
                  last4: value.token_last4,
                })}
              </p>
            ) : (
              <p id="agent-token-help" className="text-xs text-fg-muted">
                {t("El token se cifra en el servidor y nunca vuelve a mostrarse completo.")}
              </p>
            )}

            {isNew && (
              <p className="text-[12px] md:text-[11px] text-fg-muted">
                {t("Guarda el agente para probar la conexión.")}
              </p>
            )}
          </div>
        </>
      )}
    </section>
  );
}
